import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { BehaviorSubject, Observable, throwError } from 'rxjs';
import { map, catchError, tap } from 'rxjs/operators';
import { environment } from '@env/environment';
import { AuthRepository } from 'app/core/auth/auth.repository';

// ─── Types ─────────────────────────────────────────────

export interface StudentPointsConfig {
  course_start:           number;
  course_finish:          number;
  clear_word:             number;
  read_text:              number;
  read_pdf:               number;
  read_pdf_per_page:      number;
  watch_video:            number;
  watch_video_per_minute: number;
  gallery_per_slide:      number;
  write_essay:            number;
  drill_yourself:         number;
  drill_comp:             number;
  quiz:                   number;
  final_test:             number;
}

export interface SystemSettings {
  featuresTopNav: boolean;
  feature_firststart: boolean;
  feature_classroom: boolean;
  feature_clearword: boolean;
  feature_chat_supervisor: boolean;
  feature_chat_twin: boolean;
  feature_progress_rocket: boolean;
  feature_dashboard_courses: boolean;
  feature_badges: boolean;
  feature_ai: boolean;
  feature_header_white_bg: boolean;
  feature_supervisor: boolean;
  feature_twin: boolean;
  feature_cross_company: boolean;
  /** Email debug / test-mode — editable by SuperUser/SysAdmin only */
  email_debug_enabled?: boolean;
  email_debug_test_email?: string;
  email_debug_redirect_all?: boolean;
  email_debug_log_recipients?: boolean;
  /** System-wide default point values awarded on step completion */
  student_points?: StudentPointsConfig;
  [key: string]: any;
}

export interface CompanySettings {
  branding: {
    brand_color_1:    string;
    brand_color_2:    string;
    brand_color_3:    string;
    brand_logo:       string;
    brand_logo_light: string;
    brand_logo_dark:  string;
    login_background: string;
    favicon?:         string;  // media_id — browser tab favicon (shown after login)
    white_navigation: boolean;
  };
  languages: {
    available: string[];
    default: string;
  };
  /** Per-company feature flag overrides. null = inherit from system. */
  features: {
    [key: string]: boolean | null;
  };
  onboarding: {
    page_welcome: boolean;
    page_culture_settings: boolean;
    page_student_data_form: boolean;
    page_check_phone: boolean;
    page_check_creditcard: boolean;
    page_check_email: boolean;
    page_terms_and_conditions: boolean;
    page_check_audio_video: boolean;
    page_profile_image: boolean;
    page_scheduler: boolean;
    page_course_info: boolean;
    page_clear_word_intro: boolean;
    page_start: boolean;
    [key: string]: boolean;
  };
  welcome_page: {
    title: string;
    subtitle: string;
    hero_image: string;
  };
  validation: {
    validate_phone: boolean;
    validate_creditcard: boolean;
    validate_email: boolean;
    confirm_message_send?: boolean;
  };
  notifications?: {
    enabled: boolean;
    events?: {
      student_submission?: boolean;
      supervisor_approval?: boolean;
      supervisor_rejection?: boolean;
      student_resubmission?: boolean;
    };
    step_types?: {
      watch_video?: boolean;
      read_text?: boolean;
      read_pdf?: boolean;
      write_essay?: boolean;
      do_drill?: boolean;
      checkout?: boolean;
      course_start?: boolean;
      completion?: boolean;
      drill_skype?: boolean;
      glossary?: boolean;
      policy?: boolean;
      youtube?: boolean;
      quiz?: boolean;
      final_test?: boolean;
      how_are_you?: boolean;
      twinning?: boolean;
      supervisor?: boolean;
      gallery?: boolean;
      badge?: boolean;
    };
  };
  terms_and_conditions: {
    current_version:    string;
    versions: Array<{
      version:    string;
      content:    Record<string, string>; // lang → HTML
      created_at: string;
    }>;
    terms_enabled?:     boolean;
    terms_pdf_media_id?: string;
    gdpr_enabled?:      boolean;
    gdpr_content?:      Record<string, string> | string;
    gdpr_pdf_media_id?: string;
  };
  /** Per-company point overrides. null = inherit from system default. */
  student_points?: { [K in keyof StudentPointsConfig]: number | null };
  copyright: string;
  contact_email: string;
  contact_name: string;
  company_info: {
    name: string;
    email: string;
    phone: string;
    address: string;
    payment_info: string;
  };
  session?: {
    inactivity_timeout_minutes?: number; // 0 = disabled; default 15
    vc_track_idle_minutes?: number;       // LiveKit track-pause threshold; 0 = disabled; default 5
  };
  messaging?: {
    ack_required?: boolean; // Students must click OK to acknowledge supervisor/twin messages (default: true)
  };
  [key: string]: any;
}

/** Response shape from GET /company-settings/{id} */
export interface CompanySettingsResponse {
  data: CompanySettings;
  editable_keys: string[];
  system_features: SystemSettings;
  system_languages: string[];
  company_id: number;
}

// Simple XOR-based obfuscation for localStorage (not cryptographic, but prevents plain-text reading)
const STORAGE_KEY_SYS = 'st_sys_cfg';
const STORAGE_KEY_COMPANY = 'st_cmp_cfg';
const OBFUSCATION_KEY = 'sTuDeMa2026!';

function obfuscate(data: string): string {
  let result = '';
  for (let i = 0; i < data.length; i++) {
    result += String.fromCharCode(
      data.charCodeAt(i) ^ OBFUSCATION_KEY.charCodeAt(i % OBFUSCATION_KEY.length)
    );
  }
  return btoa(result);
}

function deobfuscate(encoded: string): string {
  try {
    const data = atob(encoded);
    let result = '';
    for (let i = 0; i < data.length; i++) {
      result += String.fromCharCode(
        data.charCodeAt(i) ^ OBFUSCATION_KEY.charCodeAt(i % OBFUSCATION_KEY.length)
      );
    }
    return result;
  } catch {
    return '{}';
  }
}

const httpOptions = {
  headers: new HttpHeaders({ 'Content-Type': 'application/json' }),
};

// ─── Service ───────────────────────────────────────────

@Injectable({ providedIn: 'root' })
export class FeatureSettingsService {
  private baseUrl = environment.api_baseurl;

  private _systemSettings$ = new BehaviorSubject<SystemSettings | null>(null);
  private _companySettings$ = new BehaviorSubject<CompanySettings | null>(null);

  /** Observable of current system settings */
  systemSettings$ = this._systemSettings$.asObservable();
  /** Observable of current company settings */
  companySettings$ = this._companySettings$.asObservable();

  constructor(private http: HttpClient, private authRepository: AuthRepository) {
    // Try to restore from localStorage on startup
    this.restoreFromStorage();
  }

  // ═══════════════════════════════════════════════════════
  //  INITIALIZATION (called after login)
  // ═══════════════════════════════════════════════════════

  /**
   * Called from the login flow to store settings received in the login response.
   */
  initFromLoginResponse(loginData: any): void {
    if (loginData.system_settings) {
      this._systemSettings$.next(loginData.system_settings as SystemSettings);
      this.saveToStorage(STORAGE_KEY_SYS, loginData.system_settings);
    }
    if (loginData.company_settings) {
      this._companySettings$.next(loginData.company_settings as CompanySettings);
      this.saveToStorage(STORAGE_KEY_COMPANY, loginData.company_settings);
      this.applyFaviconBranding(loginData.company_settings as CompanySettings);
    }
  }

  /**
   * Restore settings from encrypted localStorage.
   */
  private restoreFromStorage(): void {
    try {
      const sysRaw = localStorage.getItem(STORAGE_KEY_SYS);
      if (sysRaw) {
        const parsed = JSON.parse(deobfuscate(sysRaw));
        this._systemSettings$.next(parsed);
      }
    } catch {
      /* ignore */
    }

    try {
      const cmpRaw = localStorage.getItem(STORAGE_KEY_COMPANY);
      if (cmpRaw) {
        const parsed = JSON.parse(deobfuscate(cmpRaw));
        this._companySettings$.next(parsed);
        this.applyFaviconBranding(parsed);
      }
    } catch {
      /* ignore */
    }
  }

  private saveToStorage(key: string, data: any): void {
    try {
      localStorage.setItem(key, obfuscate(JSON.stringify(data)));
    } catch {
      /* ignore */
    }
  }

  /**
   * Clear all stored settings (on logout).
   */
  clearSettings(): void {
    this._systemSettings$.next(null);
    this._companySettings$.next(null);
    localStorage.removeItem(STORAGE_KEY_SYS);
    localStorage.removeItem(STORAGE_KEY_COMPANY);
    this.applyFaviconBranding(null);
  }

  /**
   * Dynamically updates the browser-tab favicon to the company-specific one after login,
   * and restores the default static favicon on logout or when no favicon is configured.
   */
  private applyFaviconBranding(settings: CompanySettings | null): void {
    try {
      const faviconId = settings?.branding?.favicon;
      const links = Array.from(document.querySelectorAll<HTMLLinkElement>("link[rel~='icon']"));
      if (faviconId) {
        const token = this.authRepository.getAccessToken();
        const url   = `${environment.serverUrl}/v1/company-media/${faviconId}?token=${token}`;
        links.forEach(link => { link.href = url; });
        if (links.length === 0) {
          const link = document.createElement('link');
          link.rel  = 'icon';
          link.type = 'image/png';
          link.href = url;
          document.head.appendChild(link);
        }
      } else {
        // Restore static defaults
        links.forEach((link, i) => {
          link.href = i === 0 ? 'favicon-16x16.png' : 'favicon-32x32.png';
        });
      }
    } catch {
      /* ignore — DOM access may not be available in SSR or test environments */
    }
  }

  // ═══════════════════════════════════════════════════════
  //  FEATURE FLAG GETTERS
  //  Uses: DB settings > environment.ts fallback
  // ═══════════════════════════════════════════════════════

  /**
   * Get a feature flag value.
   * Resolution order: company-level override → system-level → environment fallback.
   * A company override of `null` means "inherit from system".
   */
  getFeature(key: string): any {
    // 1. Company-level override (null = not set = inherit from system)
    const company = this._companySettings$.getValue();
    const companyOverride = company?.features?.[key];
    if (companyOverride !== undefined && companyOverride !== null) {
      return companyOverride;
    }

    // 2. System-level setting
    const sys = this._systemSettings$.getValue();
    if (sys && key in sys) {
      return sys[key];
    }

    // 3. Environment fallback
    return (environment as any)[key];
  }

  /** Shorthand boolean check */
  isFeatureEnabled(key: string): boolean {
    return !!this.getFeature(key);
  }

  /**
   * Get the VC track-idle timeout (minutes) from company settings.
   * Falls back to 5 minutes. 0 = disabled.
   */
  getVcTrackIdleMinutes(): number {
    const company = this._companySettings$.getValue();
    const configured = (company?.session as any)?.vc_track_idle_minutes;
    return configured !== undefined && configured !== null ? (configured as number) : 5;
  }

  /** Get all current system settings (merged with environment defaults) */
  getSystemSettings(): SystemSettings {
    const sys = this._systemSettings$.getValue();
    const envDefaults: Partial<SystemSettings> = {
      featuresTopNav: (environment as any).featuresTopNav ?? true,
      feature_firststart: (environment as any).feature_firststart ?? false,
      feature_classroom: (environment as any).feature_classroom ?? false,
      feature_clearword: (environment as any).feature_clearword ?? false,
      feature_chat_supervisor: (environment as any).feature_chat_supervisor ?? false,
      feature_chat_twin: (environment as any).feature_chat_twin ?? false,
      feature_progress_rocket: (environment as any).feature_progress_rocket ?? false,
      feature_dashboard_courses: (environment as any).feature_dashboard_courses ?? false,
      feature_badges: (environment as any).feature_badges ?? false,
      feature_ai: (environment as any).feature_ai ?? false,
      feature_header_white_bg: (environment as any).feature_header_white_bg ?? false,
      feature_supervisor: (environment as any).feature_supervisor ?? false,
      feature_twin: (environment as any).feature_twin ?? false,
      feature_cross_company: (environment as any).feature_cross_company ?? false,
    };
    return { ...envDefaults, ...(sys || {}) } as SystemSettings;
  }

  /** Get current company settings */
  getCompanySettings(): CompanySettings | null {
    return this._companySettings$.getValue();
  }

  /**
   * Returns the inactivity timeout in minutes for the current user's company.
   * Returns 0 if auto-logout is disabled.
   * Falls back to 15 minutes if not configured.
   */
  getInactivityTimeoutMinutes(): number {
    const company = this._companySettings$.getValue();
    const configured = company?.session?.inactivity_timeout_minutes;
    if (configured === undefined || configured === null) {
      return 15;
    }
    return configured;
  }

  /**
   * True when the company requires students to explicitly click OK to acknowledge
   * supervisor messages (company_settings.messaging.ack_required).
   */
  get ackEnabled(): boolean {
    return !!(this._companySettings$.getValue()?.messaging?.ack_required);
  }

  // ═══════════════════════════════════════════════════════
  //  HTTP OPERATIONS (Admin UI)
  // ═══════════════════════════════════════════════════════

  /** GET /system-settings — fetch from server (admin only) */
  fetchSystemSettings(): Observable<SystemSettings> {
    console.log('[FeatureSettingsService] fetchSystemSettings GET...');
    return this.http
      .get<any>(`${this.baseUrl}/system-settings`, httpOptions)
      .pipe(
        tap((rawRes) => {
          console.log('[FeatureSettingsService] fetchSystemSettings RAW RESPONSE:', JSON.stringify(rawRes));
          console.log('[FeatureSettingsService] feature_firststart from server =', rawRes?.data?.feature_firststart);
        }),
        map((res) => res.data as SystemSettings),
        tap((settings) => {
          console.log('[FeatureSettingsService] fetchSystemSettings PARSED:', JSON.stringify(settings));
          this._systemSettings$.next(settings);
          this.saveToStorage(STORAGE_KEY_SYS, settings);
        }),
        catchError(this.handleError)
      );
  }

  /** POST /system-settings — save to server (admin only) */
  saveSystemSettings(settings: Partial<SystemSettings>): Observable<SystemSettings> {
    console.log('[FeatureSettingsService] saveSystemSettings SENDING:', JSON.stringify({ settings }));
    return this.http
      .post<any>(
        `${this.baseUrl}/system-settings`,
        JSON.stringify({ settings }),
        httpOptions
      )
      .pipe(
        tap((rawRes) => {
          console.log('[FeatureSettingsService] saveSystemSettings RAW RESPONSE:', JSON.stringify(rawRes));
          if (rawRes?._debug) {
            console.log('[FeatureSettingsService] === SERVER DEBUG ===');
            (rawRes._debug as string[]).forEach((d: string) => console.log('  ', d));
          }
        }),
        map((res) => res.data as SystemSettings),
        tap((saved) => {
          console.log('[FeatureSettingsService] saveSystemSettings PARSED:', JSON.stringify(saved));
          this._systemSettings$.next(saved);
          this.saveToStorage(STORAGE_KEY_SYS, saved);
        }),
        catchError(this.handleError)
      );
  }

  /** GET /company-settings/{id} — fetch company settings */
  fetchCompanySettings(companyId: number): Observable<CompanySettingsResponse> {
    return this.http
      .get<any>(`${this.baseUrl}/company-settings/${companyId}`, httpOptions)
      .pipe(
        map((res) => ({
          data: res.data as CompanySettings,
          editable_keys: res.editable_keys || [],
          system_features: res.system_features as SystemSettings,
          system_languages: (res.system_languages || []) as string[],
          company_id: res.company_id || companyId,
        } as CompanySettingsResponse)),
        tap((result) => {
          this._companySettings$.next(result.data);
          this.saveToStorage(STORAGE_KEY_COMPANY, result.data);
        }),
        catchError(this.handleError)
      );
  }

  /** POST /company-settings/{id} — save company settings */
  saveCompanySettings(companyId: number, settings: Partial<CompanySettings>): Observable<CompanySettings> {
    return this.http
      .post<any>(
        `${this.baseUrl}/company-settings/${companyId}`,
        JSON.stringify({ settings }),
        httpOptions
      )
      .pipe(
        map((res) => res.data as CompanySettings),
        tap((saved) => {
          this._companySettings$.next(saved);
          this.saveToStorage(STORAGE_KEY_COMPANY, saved);
          this.applyFaviconBranding(saved);
        }),
        catchError(this.handleError)
      );
  }

  /** POST /company-settings/{id}/logo — upload brand logo */
  uploadCompanyLogo(companyId: number, file: File): Observable<{ logo_path: string }> {
    const formData = new FormData();
    formData.append('logo', file);

    return this.http
      .post<any>(`${this.baseUrl}/company-settings/${companyId}/logo`, formData)
      .pipe(
        map((res) => res.data),
        catchError(this.handleError)
      );
  }

  // ─── Helpers ──────────────────────────────────────────

  private handleError(error: any): Observable<never> {
    console.error('[FeatureSettingsService]', error);
    return throwError(() => error);
  }
}

